"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { ImageDetailModal } from "@saas/shared/assets/ImageDetailModal";
import { VideoDetailModal } from "@saas/shared/assets/VideoDetailModal";
import {
	type FeedView,
	HistoryFeedTabs,
} from "@saas/shared/history/HistoryFeedTabs";
import { ImageHistoryCard } from "@saas/shared/history/ImageHistoryCard";
import { VideoHistoryCard } from "@saas/shared/history/VideoHistoryCard";
import { WorkbenchTemplates } from "@saas/shared/history/WorkbenchTemplates";
import {
	type GenerationHistoryItem,
	type HistoryFilter,
	IMAGE_HISTORY_PAGE_SIZE,
	type ImageHistoryItem,
	type VideoHistoryItem,
	groupHistoryEntries,
	historyQueryKey,
} from "@saas/shared/history/types";
import { useImageHistoryActions } from "@saas/shared/history/use-image-history-actions";
import { useResumeHistoryTasks } from "@saas/shared/history/use-resume-history-tasks";
import { useVideoFavorite } from "@saas/shared/history/use-video-favorite";
import { useVideoHomeRouter } from "@saas/shared/history/video-home";
import { apiClient } from "@shared/lib/api-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

/** The standalone image effects share the same feed tabs and history actions as the studio. */
export function ImageEffectFeed({ preview }: { preview: ReactNode }) {
	const { user } = useSession();
	const t = useTranslations("assets");
	const [view, setView] = useState<FeedView | null>(null);
	const [filter, setFilter] = useState<HistoryFilter>("all");
	const [selectedImage, setSelectedImage] = useState<{
		item: ImageHistoryItem;
		index: number;
	} | null>(null);
	const [selectedVideo, setSelectedVideo] = useState<VideoHistoryItem | null>(
		null,
	);
	const imageActions = useImageHistoryActions({
		onDeleted: () => setSelectedImage(null),
	});
	const videoFavorite = useVideoFavorite();
	const routeVideoHome = useVideoHomeRouter();
	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: historyQueryKey(IMAGE_HISTORY_PAGE_SIZE, filter),
		enabled: Boolean(user),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await apiClient.generations.history.$get({
				query: {
					page: String(pageParam),
					limit: String(IMAGE_HISTORY_PAGE_SIZE),
					filter,
				},
			});
			if (!response.ok) {
				throw new Error("Failed to fetch history");
			}
			return response.json();
		},
		getNextPageParam: (last) =>
			last.pagination.page < last.pagination.totalPages
				? last.pagination.page + 1
				: undefined,
	});
	const history =
		data?.pages.flatMap((page) => page.data as GenerationHistoryItem[]) ??
		[];
	const activeView = user
		? (view ?? (isLoading || history.length ? "mine" : "examples"))
		: view === "templates"
			? "templates"
			: "examples";
	useResumeHistoryTasks(history, "image");
	useResumeHistoryTasks(history, "video");
	const { ref: loadMoreRef, inView } = useInView({ rootMargin: "200px" });
	useEffect(() => {
		if (
			activeView === "mine" &&
			inView &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			void fetchNextPage();
		}
	}, [activeView, inView, hasNextPage, isFetchingNextPage, fetchNextPage]);
	const actionLabels = {
		generateVideo: t("detail.generateVideo"),
		download: t("buttons.download"),
		more: t("buttons.more"),
		edit: t("buttons.edit"),
		delete: t("delete"),
		favorite: t("buttons.favorite"),
	};
	return (
		<>
			<div className="shrink-0 p-4 sm:p-6">
				<div className="mx-auto max-w-5xl">
					<HistoryFeedTabs
						view={activeView}
						onViewChange={setView}
						showHistory={!!user}
						filter={filter}
						onFilterChange={(next) => {
							setView("mine");
							setFilter(next);
						}}
					/>
				</div>
			</div>
			{activeView === "templates" ? (
				<WorkbenchTemplates kind="image" className="px-4 sm:px-6" />
			) : activeView === "examples" ? (
				<div className="flex min-h-[360px] flex-1 flex-col overflow-y-auto p-4 sm:p-6">
					<div className="m-auto w-full max-w-4xl shrink-0">
						{preview}
					</div>
				</div>
			) : (
				<div className="min-h-[360px] flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
					<div className="mx-auto max-w-5xl space-y-4 sm:space-y-6">
						{isLoading ? (
							<div className="flex min-h-[320px] items-center justify-center">
								<span className="loading loading-dots loading-lg text-blue-500" />
							</div>
						) : isError ? (
							<p
								role="alert"
								className="py-12 text-center text-muted-foreground"
							>
								{t("errors.fetchFailed")}
							</p>
						) : history.length === 0 ? (
							<p className="py-12 text-center text-muted-foreground">
								{t("emptyFiltered")}
							</p>
						) : (
							groupHistoryEntries(history).map((entry) =>
								entry.kind === "videos" ? (
									<VideoHistoryCard
										key={entry.items[0].id}
										items={entry.items}
										actionLabels={actionLabels}
										onReprompt={(item) => {
											routeVideoHome(item, "reprompt");
										}}
										onRegenerate={(item) => {
											routeVideoHome(item, "regenerate");
										}}
										onOpenDetail={setSelectedVideo}
										onToggleFavorite={
											videoFavorite.toggleFavorite
										}
										isFavorite={videoFavorite.isFavorite}
									/>
								) : (
									<ImageHistoryCard
										key={entry.item.id}
										item={entry.item}
										actionLabels={actionLabels}
										onOpenDetail={(index) =>
											setSelectedImage({
												item: entry.item,
												index,
											})
										}
										openDetailLabel={t("detail.title")}
										onReprompt={() =>
											imageActions.repromptOrRegenerate(
												entry.item,
												"reprompt",
											)
										}
										onRegenerate={() =>
											imageActions.repromptOrRegenerate(
												entry.item,
												"regenerate",
											)
										}
										onDownload={imageActions.download}
										onEdit={imageActions.edit}
										onGenerateVideo={
											imageActions.generateVideo
										}
										onDelete={(index) =>
											imageActions.deleteImage(
												entry.item.id,
												index,
											)
										}
										onToggleFavorite={(index) =>
											imageActions.toggleFavorite(
												entry.item,
												index,
											)
										}
										isImageFavorite={(index) =>
											imageActions.isImageFavorite(
												entry.item,
												index,
											)
										}
									/>
								),
							)
						)}
						{hasNextPage && (
							<div ref={loadMoreRef} className="h-8 text-center">
								{isFetchingNextPage && (
									<span className="loading loading-spinner loading-sm" />
								)}
							</div>
						)}
					</div>
				</div>
			)}
			{selectedImage && (
				<ImageDetailModal
					isOpen
					onClose={() => setSelectedImage(null)}
					generation={selectedImage.item}
					initialImageIndex={selectedImage.index}
					onReprompt={() =>
						imageActions.repromptOrRegenerate(
							selectedImage.item,
							"reprompt",
						)
					}
					onRegenerate={() =>
						imageActions.repromptOrRegenerate(
							selectedImage.item,
							"regenerate",
						)
					}
					onGenerateVideo={imageActions.generateVideo}
					onDelete={(index) =>
						imageActions.deleteImage(selectedImage.item.id, index)
					}
					onToggleFavorite={(index) =>
						imageActions.toggleFavorite(selectedImage.item, index)
					}
					isImageFavorite={(index) =>
						imageActions.isImageFavorite(selectedImage.item, index)
					}
				/>
			)}
			{selectedVideo && (
				<VideoDetailModal
					isOpen
					onClose={() => setSelectedVideo(null)}
					video={selectedVideo}
					onReprompt={() => {
						routeVideoHome(selectedVideo, "reprompt");
					}}
					onRegenerate={() => {
						routeVideoHome(selectedVideo, "regenerate");
					}}
					isFavorite={videoFavorite.isFavorite(selectedVideo)}
					onToggleFavorite={() =>
						videoFavorite.toggleFavorite(selectedVideo)
					}
				/>
			)}
		</>
	);
}
